import fillNullCashflowData from '../helpers/fillNullCashflowData'
import formatNumber from '../helpers/formatNumber'
import findDiffs from '../helpers/findDiffs'
import fillNullDiffs from '../helpers/fillNullDiffs'
import years from './testData/years'
import getEntityNames from '../helpers/getEntityNames'
import getYearSources from '../helpers/getYearSources'

import business from './testData/business'


describe('fillNullCashflowData', () => {
  const category = 'business_debt'
  it('creates an array of empty cashflow data if the provided data array is empty', () => {
    const filledCashflowData = fillNullCashflowData([], years, category)
    expect(filledCashflowData.length).toEqual(years.size)
  })
  it('fills in empty cashflow data for years that do not have any', () => {
    const data = [
      { category, data: 200, createdAt: 13144114, resourceType: 'Business', source: 'keyed', year: 2015, period: 'tax' },
      { category, data: 200, createdAt: 13144114, resourceType: 'Business', source: 'keyed', year: 2016, period: 'tax' }
    ]
    const filledCashflowData = fillNullCashflowData(data, years, category)
    expect(filledCashflowData.length).toEqual(years.size)
    for (const datum of filledCashflowData) {
      expect(typeof datum.year).toEqual('number')
      expect(datum.category).toEqual(category)
      expect(datum.period).toEqual(years.get(datum.year).period)
    }
  })
  it('throws an error the provided data is not an array', () => {
    expect(() => fillNullCashflowData(years, category)).toThrow()
    expect(() => fillNullCashflowData(4, years, category)).toThrow()
    expect(() => fillNullCashflowData(undefined, years, category)).toThrow()
  })
  it('returns cashflow data sorted by year in ascending order', () => {
    const filledCashflowData = fillNullCashflowData([], years, category)
    let currentYear = 0
    for (const datum of filledCashflowData) {
      expect(datum.year > currentYear).toBeTruthy()
      currentYear = datum.year
    }
  })
})
describe('formatNumber', () => {
  it('returns a comma separated number for numbers 1000 or greater', () => {
    const number = 2000
    expect(formatNumber(number)).toEqual('2,000')
  })
  it('encloses negative numbers in parentheses', () => {
    const number = -20000
    expect(formatNumber(number)).toEqual('(20,000)')
  })
})
describe('findDiffs', () => {
  it('should return diffs if there is data from the same year and category if the most recent data comes from an untrusted source and the second most recent data comes from a trusted source', () => {
    const fiscalYears = [
      { period: 'tax', year: 2015 },
      { period: 'tax', year: 2018 }
    ]
    const dataPairs = [
      {
        data: [
          { source: 'tax_transcript', period: 'tax', category: 'gross_revenue', year: 2015, data: 1000000, createdAt: 1520883960, resourceType: 'Business', accountable: { name: '' } },
          { source: 'keyed', period: 'tax', category: 'gross_revenue', year: 2015, data: 202020, createdAt: 1520983960, resourceType: 'Business', accountable: { name: '' } }
        ],
        expected: { diff: -797980, year: 2015, category: 'gross_revenue', discrepancy: false, accountable: '', lastEdited: '03/13/2018' }
      },
      {
        data: [
          { source: 'extracted_document', period: 'tax', category: 'cogs', year: 2018, data: 100, createdAt: 1520883960, resourceType: 'Business', accountable: { name: '' } },
          { source: 'keyed', period: 'tax', category: 'cogs', year: 2018, data: 50, createdAt: 1520983960, resourceType: 'Business', accountable: { name: '' } }
        ],
        expected: { diff: -50, year: 2018, category: 'cogs', discrepancy: false, accountable: '', lastEdited: '03/13/2018' }
      },
      {
        data: [
          { source: 'manual_document', period: 'tax', category: 'cogs', year: 2018, data: 2000, createdAt: 1520883960, resourceType: 'Business', accountable: { name: '' } },
          { source: 'keyed', period: 'tax', category: 'cogs', year: 2018, data: 3000, createdAt: 1520983960, resourceType: 'Business', accountable: { name: '' } }
        ],
        expected: { diff: 1000, year: 2018, category: 'cogs', discrepancy: false, accountable: '', lastEdited: '03/13/2018' }
      }
    ]
    for (const pair of dataPairs) {
      const [diff] = findDiffs(pair.data, fiscalYears)
      expect(diff).toEqual(pair.expected)
    }
  })
  it('should throw an error if it is passed something that\'s not an array', () => {
    expect(() => findDiffs({})).toThrow()
  })
})
describe('fillNullDiffs', () => {
  it('should create an array of empty diffs if it is passed an empty array', () => {
    const filledDiffs = fillNullDiffs([], years)
    expect(filledDiffs.length).toEqual(years.size)
  })
  it('should add an empty diff for any year that does not have any diff associated with it', () => {
    const emptyYears = [2015, 2016, 2018]
    const diffs = [
      { diff: 200, year: 2017, category: 'gross_revenue', discrepancy: false },
      { diff: 90000, year: 2014, category: 'gross_revenue', discrepancy: true }
    ]
    const filledDiffs = fillNullDiffs(diffs, years).filter(item => item.diff !== null)
    expect(filledDiffs.length).toEqual(diffs.length)
  })
})
describe('getEntityNames', () => {
  it('should generate an array of objects with properly formatted entity names', () => {
    const entities = [
      {
        name: 'This is a business',
        borrowerType: 'oc',
        __typename: 'Business',
        rid: 1
      },
      {
        name: 'This is an affiliate',
        borrowerType: 'affiliate',
        __typename: 'Business',
        rid: 2
      },
      {
        name: 'This is another affiliate',
        borrowerType: 'affiliate',
        __typename: 'Business',
        rid: 3
      },
      {
        name: 'This is an EPC',
        borrowerType: 'epc',
        __typename: 'Business',
        rid: 4
      },
      {
        name: 'The Guarantor',
        __typename: 'Guarantor',
        rid: 1
      }
    ]
    const namedEntities = getEntityNames(entities)
    expect(namedEntities[0].name).toEqual(entities[0].name)
    expect(namedEntities[0].title).toEqual('OC')
    expect(namedEntities[0].key).toEqual('Business1')

    expect(namedEntities[1].name).toEqual(entities[1].name)
    expect(namedEntities[1].title).toEqual('Affiliate 1')
    expect(namedEntities[1].key).toEqual('Business2')

    expect(namedEntities[2].name).toEqual(entities[2].name)
    expect(namedEntities[2].title).toEqual('Affiliate 2')
    expect(namedEntities[2].key).toEqual('Business3')

    expect(namedEntities[3].name).toEqual(entities[3].name)
    expect(namedEntities[3].title).toEqual('EPC 1')
    expect(namedEntities[3].key).toEqual('Business4')

    expect(namedEntities[4].name).toEqual(entities[4].name)
    expect(namedEntities[4].title).toBe(undefined)
    expect(namedEntities[4].key).toEqual('Guarantor1')

  })

  describe('#getYearSources', () => { // This could use more rigorous testing. Alternately, we could find a solution other than deriving information from data layers
    it('should return a properly formatted object with the source and period of each year', () => {
      const props = {
        entity: business, // This business has manual documents for every year and 2018 as an interim year
        resourceType: 'Business'
      }
      const yearSources = getYearSources(props)
      for (const year of business.applicableYears) {
        const sourcesForYear = business.cashflowData.filter(d => d.year === year).map(d => d.source)
        const { period, source } = yearSources[year]
        if (sourcesForYear.includes('tax_transcript')) { // Check for correct source
          expect(source).toBe('tax_transcript')
        } else if (sourcesForYear.includes('manual_transcript')) {
          expect(source).toBe('manual_transcript')
        } else {
          expect(source).toBe('manual_document')
        }

        if (business.interimYears.includes(year)) { // Check for correct period
          expect(period).toBe('interim')
        } else if (!business.interimYears.includes(year)) {
          expect(period).toBe('tax')
        }
      }
    })
  })
})
